import React, { useState, useEffect } from "react";
import { Form, InputNumber, Button, Switch, Divider, Space, Input, Card, Row, Col, Typography, Tooltip } from "antd";
import { 
    PlusOutlined, 
    SettingOutlined, 
    CarOutlined, 
    EuroOutlined, 
    ControlOutlined, 
    SafetyOutlined, 
    ClockCircleOutlined, 
    RocketOutlined,
    DeleteOutlined,
    InfoCircleOutlined,
    WalletOutlined,
    GlobalOutlined
} from "@ant-design/icons";
import { PrimaryButton } from "../../component/ui/ActionButtons";
import Breadcrumbs from "../../component/Breadcrumbs";
import DeliverySettingsServ from "../../service/DeliverySettingsServ";
import Notify from "../../util/Notify";

const { Text, Title } = Typography;

const DeliverySettings = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const res = await DeliverySettingsServ.get();
                console.log("Fetched settings:", res);
                if (res) {
                    const formData = {
                        baseKmLimit: res.baseKmLimit || 5,
                        baseKmFee: res.baseKmFee || 20,
                        extraKmFee: res.extraKmFee || 10,
                        freeThreshold: res.freeDeliveryThreshold || 0,
                        handlingFee: res.handlingFee || 0,
                        freeHandlingThreshold: res.freeHandlingThreshold || 0,
                        radius: res.maxDeliveryDistance || res.deliveryRadius || 0,
                        isActive: res.isDeliveryEnabled !== undefined ? res.isDeliveryEnabled : true,
                        autoAssignDriver: res.autoAssignDriver || false,
                        defaultDeliveryTime: res.defaultDeliveryTime || 24,
                        feeRules: res.feeRules || []
                    };
                    console.log("Setting form values:", formData);
                    form.setFieldsValue(formData);
                }
            } catch (error) {
                console.error("Error fetching delivery settings:", error);
            }
        };
        fetchSettings();
    }, [form]);

    const onFinish = async (values) => {
        console.log("✅ Form submitted with values:", values);
        setLoading(true);

        try {
            const payload = {
                baseKmLimit: values.baseKmLimit || 5,
                baseKmFee: values.baseKmFee || 20,
                extraKmFee: values.extraKmFee || 10,
                freeDeliveryThreshold: values.freeThreshold || 0,
                handlingFee: values.handlingFee || 0,
                freeHandlingThreshold: values.freeHandlingThreshold || 0,
                maxDeliveryDistance: values.radius || 0,
                isDeliveryEnabled: values.isActive !== undefined ? values.isActive : true,
                autoAssignDriver: values.autoAssignDriver || false,
                defaultDeliveryTime: values.defaultDeliveryTime || 24,
                feeRules: values.feeRules || []
            };

            console.log("📤 Sending payload:", payload);

            const response = await DeliverySettingsServ.update(payload);
            console.log("✅ Save response:", response);

            // Show success notification
            Notify.success('Delivery settings have been saved successfully');

        } catch (error) {
            console.error("❌ Save error:", error);
            // Show error notification
            Notify.error(error, "Failed to update settings");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="pcu-padding-24">
            <div className="pcu-margin-bottom-16">
                <Breadcrumbs />
            </div>

            <div className="sd-container" style={{ padding: '24px' }}>
                <div className="sd-header" style={{ marginBottom: '24px', background: 'transparent', padding: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                        <div className="sd-orders-icon-wrap" style={{ width: '48px', height: '48px', background: '#fff', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                            <SettingOutlined style={{ fontSize: '22px', color: '#4776E6' }} />
                        </div>
                        <div>
                            <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#1a1d2e', letterSpacing: '-0.5px' }}>Delivery Settings</h1>
                            <p style={{ margin: 0, fontSize: '12px', color: '#9ca3af', fontWeight: 500 }}>Configure rules, fees, and automation</p>
                        </div>
                    </div>
                </div>

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    size="large"
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                        {/* Section 1: Service Controls */}
                        <div className="pcu-bg-white pcu-radius-20 pcu-shadow-sm" style={{ padding: '24px', border: '1px solid #f1f5f9' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                                <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <ControlOutlined style={{ color: '#4f46e5', fontSize: '16px' }} />
                                </div>
                                <Text style={{ fontSize: '16px', fontWeight: 700, color: '#1e293b' }}>Service Configuration</Text>
                            </div>
                            
                            <Row gutter={24}>
                                <Col span={12}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', borderRadius: '12px', background: '#f8fafc', border: '1.5px solid #f1f5f9' }}>
                                        <div>
                                            <div style={{ fontWeight: 700, color: '#334155', fontSize: '14px' }}>Enable Local Delivery</div>
                                            <div style={{ fontSize: '12px', color: '#64748b' }}>Allow customers to choose local delivery</div>
                                        </div>
                                        <Form.Item name="isActive" valuePropName="checked" noStyle>
                                            <Switch />
                                        </Form.Item>
                                    </div>
                                </Col>
                                <Col span={12}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', borderRadius: '12px', background: '#f8fafc', border: '1.5px solid #f1f5f9' }}>
                                        <div>
                                            <div style={{ fontWeight: 700, color: '#334155', fontSize: '14px' }}>Auto-Assign Driver</div>
                                            <div style={{ fontSize: '12px', color: '#64748b' }}>Delegate pickup requests to nearest staff</div>
                                        </div>
                                        <Form.Item name="autoAssignDriver" valuePropName="checked" noStyle>
                                            <Switch />
                                        </Form.Item>
                                    </div>
                                </Col>
                            </Row>
                        </div>

                        {/* Section 2: Distance & Fees */}
                        <div className="pcu-bg-white pcu-radius-20 pcu-shadow-sm" style={{ padding: '24px', border: '1px solid #f1f5f9' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                                <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <GlobalOutlined style={{ color: '#10b981', fontSize: '16px' }} />
                                </div>
                                <Text style={{ fontSize: '16px', fontWeight: 700, color: '#1e293b' }}>Distance & Fee Structure</Text>
                            </div>

                            <Row gutter={24}>
                                <Col span={8}>
                                    <Form.Item name="baseKmLimit" label={
                                        <Text style={{ fontWeight: 600, fontSize: '13px', color: '#64748b' }}>
                                            <CarOutlined style={{ marginRight: 6 }} /> Base Distance Limit
                                        </Text>
                                    }>
                                        <InputNumber min={1} style={{ width: "100%", borderRadius: '8px' }} addonAfter="km" placeholder="5" />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name="radius" label={
                                        <Text style={{ fontWeight: 600, fontSize: '13px', color: '#64748b' }}>
                                            <RocketOutlined style={{ marginRight: 6 }} /> Max Delivery Radius
                                        </Text>
                                    }>
                                        <InputNumber min={1} style={{ width: "100%", borderRadius: '8px' }} addonAfter="km" placeholder="10" />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name="defaultDeliveryTime" label={
                                        <Text style={{ fontWeight: 600, fontSize: '13px', color: '#64748b' }}>
                                            <ClockCircleOutlined style={{ marginRight: 6 }} /> Standard Delivery Time
                                        </Text>
                                    }>
                                        <InputNumber min={1} style={{ width: "100%", borderRadius: '8px' }} addonAfter="hrs" placeholder="24" />
                                    </Form.Item>
                                </Col>

                                <Col span={8}>
                                    <Form.Item name="baseKmFee" label={
                                        <Text style={{ fontWeight: 600, fontSize: '13px', color: '#64748b' }}>
                                            <EuroOutlined style={{ marginRight: 6 }} /> Base Delivery Fee
                                        </Text>
                                    }>
                                        <InputNumber min={0} style={{ width: "100%", borderRadius: '8px' }} prefix="₹" placeholder="20" />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name="extraKmFee" label={
                                        <Text style={{ fontWeight: 600, fontSize: '13px', color: '#64748b' }}>
                                            <PlusOutlined style={{ marginRight: 6 }} /> Extra KM Charge
                                        </Text>
                                    }>
                                        <InputNumber min={0} style={{ width: "100%", borderRadius: '8px' }} prefix="₹" placeholder="10" />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name="freeThreshold" label={
                                        <Text style={{ fontWeight: 600, fontSize: '13px', color: '#64748b' }}>
                                            <SafetyOutlined style={{ marginRight: 6 }} /> Free Above Amount
                                        </Text>
                                    }>
                                        <InputNumber min={0} style={{ width: "100%", borderRadius: '8px' }} prefix="₹" placeholder="500" />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </div>

                        {/* Section 3: Handling Fee & Payout Rules */}
                        <div style={{ display: 'grid', gridTemplateColumns: '350px 1fr', gap: '24px' }}>
                            <div className="pcu-bg-white pcu-radius-20 pcu-shadow-sm" style={{ padding: '24px', border: '1px solid #f1f5f9', background: 'linear-gradient(135deg, #fffbeb 0%, #fff 100%)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                                    <div style={{ width: '30px', height: '30px', borderRadius: '6px', background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <WalletOutlined style={{ color: '#d97706', fontSize: '14px' }} />
                                    </div>
                                    <Text style={{ fontSize: '15px', fontWeight: 800, color: '#92400e' }}>Handling Rules</Text>
                                </div>
                                <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '20px', lineHeight: 1.5 }}>
                                    Processing fees applied per order to cover packing costs.
                                </Text>
                                <Form.Item name="handlingFee" label={<Text style={{ fontSize: '12px', fontWeight: 700, color: '#334155' }}>Processing Fee</Text>}>
                                    <InputNumber min={0} style={{ width: '100%', borderRadius: '8px' }} prefix="₹" placeholder="0" />
                                </Form.Item>
                                <Form.Item name="freeHandlingThreshold" label={<Text style={{ fontSize: '12px', fontWeight: 700, color: '#334155' }}>Free Processing Above</Text>}>
                                    <InputNumber min={0} style={{ width: '100%', borderRadius: '8px' }} prefix="₹" placeholder="0" />
                                </Form.Item>
                            </div>

                            {/* Section 4: Payout Rules */}
                            <div className="pcu-bg-white pcu-radius-20 pcu-shadow-sm" style={{ padding: '24px', border: '1px solid #f1f5f9' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#fff1f2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <PlusOutlined style={{ color: '#e11d48', fontSize: '16px' }} />
                                        </div>
                                        <Text style={{ fontSize: '16px', fontWeight: 700, color: '#1e293b' }}>Staff Payout Tiers</Text>
                                    </div>
                                </div>

                                <Form.List name="feeRules">
                                    {(fields, { add, remove }) => (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                            {fields.map(({ key, name, ...restField }) => (
                                                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #f1f5f9' }}>
                                                    <Col span={6}>
                                                        <Form.Item {...restField} name={[name, 'ruleType']} rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                                                            <Input placeholder="Type (Flat)" style={{ borderRadius: '6px' }} />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={5}>
                                                        <Form.Item {...restField} name={[name, 'minRange']} rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                                                            <InputNumber placeholder="Min Dist" style={{ width: '100%', borderRadius: '6px' }} addonAfter="km" />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={5}>
                                                        <Form.Item {...restField} name={[name, 'maxRange']} rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                                                            <InputNumber placeholder="Max Dist" style={{ width: '100%', borderRadius: '6px' }} addonAfter="km" />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={6}>
                                                        <Form.Item {...restField} name={[name, 'amount']} rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                                                            <InputNumber placeholder="Payout" style={{ width: '100%', borderRadius: '6px' }} prefix="₹" />
                                                        </Form.Item>
                                                    </Col>
                                                    <Button 
                                                        type="text" 
                                                        onClick={() => remove(name)} 
                                                        danger 
                                                        icon={<DeleteOutlined />} 
                                                        style={{ background: '#fff', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}
                                                    />
                                                </div>
                                            ))}
                                            <Button 
                                                type="dashed" 
                                                onClick={() => add()} 
                                                block 
                                                icon={<PlusOutlined />} 
                                                style={{ height: '44px', borderRadius: '12px', border: '2px dashed #e2e8f0', color: '#64748b', fontWeight: 600 }}
                                            >
                                                Add Payout Rule
                                            </Button>
                                        </div>
                                    )}
                                </Form.List>
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                            <PrimaryButton
                                htmlType="submit"
                                loading={loading}
                                text="Save Configuration"
                                style={{ width: '200px', height: '48px', fontSize: '15px' }}
                            />
                        </div>
                    </div>
                </Form>
            </div>
        </div>
    );
};

export default DeliverySettings;
